import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '$store/rootReducer';
import {
  AddLinkAction,
  CreateLinkAction,
  DeleteLinkAction,
  InviteBonus,
  InviteState,
  SetStatsMoreAction,
  SetBonusAction,
  SetBonusFansAction,
  SetBonusFansReferralsAction,
  SetInvitedFriendsAction,
  SetLinksAction,
  SetStatsAction,
  SetStatsDateRangeAction,
  SetStatsTableAction,
  SetStatsTableLoadingAction,
  SetSuggestionsAction,
  LoadMoreTypeAction,
  LoadMorePaginationAction,
  StopLoadMoreAction,
} from '$store/invite/interface';

const currentDate = new Date();

const initialState: InviteState = {
  referralPercent: 0,
  profit: 0,
  isGold: false,
  isSuggestionsLoading: true,
  isSuggestionsLoadingMore: false,
  suggestionsNextFrom: '',
  suggestions: [],
  isInvitedLoading: true,
  pagination: {
    friends: {
      isLoading: false,
      nextFrom: 0,
      length: 0,
    },
    stats: {
      isLoading: false,
      nextFrom: 0,
      length: 0,
    },
    statsTable: {
      isLoading: false,
      nextFrom: 0,
      length: 0,
    },
  },
  isStatsLoading: true,
  isInvitedFailed: false,
  invitedFriends: [],
  subscribed: [],
  invited: [],
  bots: [],
  totalInvitedFriends: 0,
  isStatsTableLoading: true,
  stats: {
    links: [],
    stats: [],
  },
  statsDateRange: [
    new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
    new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0),
  ],
  links: null,
  bonus: null,
  bonusFansAdded: 0,
  bonusFansEnabled: false,
  bonusFansNeed: 0,
  invitedTotal: 0,
  bonusMultiplier: 0,
  bonusFans: [],
  bonusFansReferrals: [],
  bonusFansReferralAmounts: {},
};

export const { actions, reducer } = createSlice({
  name: 'invite',
  initialState,
  reducers: {
    loadInviteStats: () => {},
    createLink: (_, action: CreateLinkAction) => {},
    deleteLinkRequest: (_, action: DeleteLinkAction) => {},
    deleteLink: (state, action: DeleteLinkAction) => {
      state.links = state.links
        ? state.links.filter((link) => link.id !== action.payload)
        : [];
    },
    addLink: (state, action: AddLinkAction) => {
      state.links = state.links ? [...state.links, action.payload] : [action.payload];
    },
    setStateDateRange: (state, action: SetStatsDateRangeAction) => {
      state.statsDateRange = action.payload;
    },
    loadMore: (state, action: LoadMoreTypeAction) => {
      state.pagination[action.payload].isLoading = true;
    },
    loadMoreStatsTable: (state, action: LoadMoreTypeAction) => {
      state.pagination[action.payload].isLoading = true;
    },
    stopLoadMore: (state, action: StopLoadMoreAction) => {
      state.pagination[action.payload.type].isLoading = false;
      state.pagination[action.payload.type].nextFrom = action.payload.nextFrom;
    },
    setMorePagination: (state, action: LoadMorePaginationAction) => {
      state.pagination[action.payload.type].nextFrom = action.payload.nextFrom;
      state.pagination[action.payload.type].length = action.payload.length;
    },
    loadInviteStatsTable: () => {},
    setStatsTableLoading: (state, action: SetStatsTableLoadingAction) => {
      state.isStatsTableLoading = action.payload;
    },
    setInviteStatsTable: (state, action: SetStatsTableAction) => {
      state.stats = action.payload;
    },
    setInviteLinks: (state, action: SetLinksAction) => {
      state.links = action.payload;
    },
    setInviteStats: (state, action: SetStatsAction) => {
      state.isGold = action.payload.isGold;
      state.profit = action.payload.profit;
      state.profit = action.payload.profit;
      state.referralPercent = action.payload.referralPercent;
      state.subscribed = action.payload.subscribed;
      state.invited = action.payload.invited;
      state.links = action.payload.links;
      state.bots = action.payload.bots.sort(
        (a, b) => b.subscriptionPrice - a.subscriptionPrice,
      );
      state.isStatsLoading = false;
    },
    setMoreInviteStats: (state, action: SetStatsMoreAction) => {
      state.invited = [...state.invited, ...action.payload.invited];
      state.links = [...(state.links || []), ...action.payload.links];
      state.bots = [...state.bots, ...action.payload.bots].sort(
        (a, b) => b.subscriptionPrice - a.subscriptionPrice,
      );
    },
    setMoreInvitedFriends: (state, action) => {
      state.invitedFriends = [...state.invitedFriends, ...action.payload.friends];
      state.totalInvitedFriends = state.invitedFriends.length;
    },
    setMoreStatsTable: (state, action: SetStatsTableAction) => {
      state.stats.stats = [...state.stats.stats, ...action.payload.stats];
      state.stats.links = [...state.stats.links, ...action.payload.links];
    },
    loadSuggestions(state) {
      state.isSuggestionsLoading = true;
      state.isSuggestionsLoadingMore = true;
    },
    setSuggestions(state, action: SetSuggestionsAction) {
      const { suggestions, nextFrom } = action.payload;

      state.suggestions = suggestions;
      state.isSuggestionsLoading = false;
      state.isSuggestionsLoadingMore = false;
      state.suggestionsNextFrom = nextFrom;
    },
    loadMoreSuggestions(state) {
      state.isSuggestionsLoadingMore = true;
    },
    setLoadedMoreSuggestions(state, action: SetSuggestionsAction) {
      const { suggestions, nextFrom } = action.payload;

      const suggestionsExist: { [index: number]: boolean } = {};
      for (let item of state.suggestions) {
        suggestionsExist[item.userId] = true;
      }

      const newSuggestions = [...state.suggestions];
      for (let item of suggestions) {
        if (!suggestionsExist[item.userId]) {
          newSuggestions.push(item);
        }
      }

      state.suggestions = newSuggestions;
      state.isSuggestionsLoadingMore = false;
      state.suggestionsNextFrom = nextFrom;
    },
    loadInvitesFriends(state) {
      state.isInvitedLoading = true;
      state.isInvitedFailed = false;
    },
    setInvitedFriends(state, action: SetInvitedFriendsAction) {
      const {
        friends,
        total,
        bonusFansAdded,
        bonusFansEnabled,
        bonusFansNeed,
        bonusMultiplier,
        invitedTotal,
      } = action.payload;

      state.isInvitedLoading = false;
      state.invitedFriends = friends;
      state.totalInvitedFriends = total;
      state.bonusFansAdded = bonusFansAdded;
      state.bonusFansEnabled = bonusFansEnabled;
      state.bonusFansNeed = bonusFansNeed;
      state.bonusMultiplier = bonusMultiplier;
      state.invitedTotal = invitedTotal;
    },
    loadInvitesFriendsFailed(state) {
      state.isInvitedLoading = false;
      state.isInvitedFailed = true;
    },
    enableGold() {},
    setBonus(state, action: SetBonusAction) {
      state.bonus = action.payload;
    },
    setBonusFans(state, action: SetBonusFansAction) {
      state.bonusFans = action.payload;
    },
    setBonusFansReferrals(state, action: SetBonusFansReferralsAction) {
      state.bonusFansReferrals = action.payload.fans;
      state.bonusFansReferralAmounts = action.payload.bonusAmounts;
    },
  },
});

export { reducer as inviteReducer, actions as inviteActions };

export const inviteSelector = (state: RootState) => state.invite;
